export interface CafeForSort {
  id: string;
  name: string;
  marketing_budget_tier?: string;
}

export interface PromotionForSort {
  cafe_id: string;
  promotion_type?: string;
  offer_tier?: string;
  setup_fee_monthly?: number;
  performance_addon_enabled?: number | boolean;
  performance_fee_per_redeemed_order?: number;
  budget_total_jod?: number;
  active?: boolean | number;
  valid_from: string;
  valid_until: string;
  created_at?: string;
  title?: string;
  campaign_placement?: string | null;
}

const TIER_MONTHLY_PROXY: Record<string, number> = {
  starter: 20,
  growth: 50,
  pro: 100,
  uncapped: 500,
};

const ROTATION_SCORE_TOLERANCE = 120;
const SLOT_WINDOW_SIZE = 20;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function normalizeTier(tier?: string): keyof typeof TIER_MONTHLY_PROXY {
  const key = String(tier || 'growth').toLowerCase();
  if (key in TIER_MONTHLY_PROXY) return key as keyof typeof TIER_MONTHLY_PROXY;
  return 'growth';
}

function isActive(p: PromotionForSort, nowIso: string): boolean {
  if (!p.active) return false;
  const now = new Date(nowIso).getTime();
  return new Date(p.valid_from).getTime() <= now && new Date(p.valid_until).getTime() >= now;
}

function isOffer(p: PromotionForSort): boolean {
  return String(p.promotion_type || 'campaign').toLowerCase() === 'offer';
}

function dateKey(nowIso: string): string {
  const d = new Date(nowIso);
  return `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;
}

function hashString(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function dailyRotationScore(cafeId: string, nowIso: string): number {
  const seed = `${dateKey(nowIso)}:${String(cafeId || '')}`;
  const h = hashString(seed);
  return (h % 10000) / 1000;
}

function normalizeBandRatios(counts: { campaign: number; offer: number; silent: number }): [number, number, number] {
  const campaign = Number(counts.campaign || 0);
  const offer = Number(counts.offer || 0);
  const silent = Number(counts.silent || 0);
  const total = campaign + offer + silent;

  if (total <= 0) return [0, 0, 1];
  const marketing = campaign + offer;
  if (marketing <= 0) return [0, 0, 1];

  const marketingShare = marketing / total;
  const campaignShareInMarketing = campaign > 0 ? campaign / marketing : 0;

  let marketingRatio = clamp(0.5 + 0.35 * marketingShare, 0.5, 0.82);
  let campaignRatioInMarketing = clamp(0.55 + 0.35 * campaignShareInMarketing, 0.55, 0.9);

  let campaignRatio = marketingRatio * campaignRatioInMarketing;
  let offerRatio = marketingRatio - campaignRatio;
  let silentRatio = 1 - marketingRatio;

  if (campaign === 0) {
    offerRatio += campaignRatio;
    campaignRatio = 0;
  }
  if (offer === 0) {
    campaignRatio += offerRatio;
    offerRatio = 0;
  }
  if (silent === 0) {
    if (campaignRatio + offerRatio <= 0) {
      campaignRatio = campaign > 0 ? 1 : 0;
      offerRatio = offer > 0 ? 1 - campaignRatio : 0;
    }
    silentRatio = 0;
  }

  if (silent > 0) {
    silentRatio = Math.max(silentRatio, 0.12);
  }
  if (offer > 0) {
    offerRatio = Math.max(offerRatio, 0.1);
  }

  const sum = campaignRatio + offerRatio + silentRatio;
  if (sum <= 0) return [0, 0, 1];
  return [campaignRatio / sum, offerRatio / sum, silentRatio / sum];
}

function allocateSlotsFromRatios(
  ratios: [number, number, number],
  counts: { campaign: number; offer: number; silent: number },
  windowSize: number
): [number, number, number] {
  const available = [counts.campaign > 0, counts.offer > 0, counts.silent > 0];
  const desired = ratios.map((r) => r * windowSize);
  const slots = desired.map((d, idx) => (available[idx] ? Math.floor(d) : 0));

  for (let i = 0; i < 3; i += 1) {
    if (available[i] && slots[i] <= 0) slots[i] = 1;
  }

  let total = slots[0] + slots[1] + slots[2];
  while (total > windowSize) {
    let idx = -1;
    let best = -Infinity;
    for (let i = 0; i < 3; i += 1) {
      if (!available[i] || slots[i] <= 1) continue;
      const overflowPriority = slots[i] - desired[i];
      if (overflowPriority > best) {
        best = overflowPriority;
        idx = i;
      }
    }
    if (idx === -1) break;
    slots[idx] -= 1;
    total -= 1;
  }

  while (total < windowSize) {
    let idx = -1;
    let best = -Infinity;
    for (let i = 0; i < 3; i += 1) {
      if (!available[i]) continue;
      const need = desired[i] - slots[i];
      if (need > best) {
        best = need;
        idx = i;
      }
    }
    if (idx === -1) break;
    slots[idx] += 1;
    total += 1;
  }

  return slots as [number, number, number];
}

function buildBandSlotCycle(
  counts: { campaign: number; offer: number; silent: number },
  windowSize = SLOT_WINDOW_SIZE
): Array<0 | 1 | 2> {
  const ratios = normalizeBandRatios(counts);
  const slotCounts = allocateSlotsFromRatios(ratios, counts, windowSize);
  const remaining = [...slotCounts];
  const used = [0, 0, 0];
  const cycle: Array<0 | 1 | 2> = [];

  while (cycle.length < windowSize) {
    let pick = -1;
    let bestScore = -Infinity;
    for (let band = 0; band < 3; band += 1) {
      if (remaining[band] <= 0) continue;
      const fairnessScore = remaining[band] / (used[band] + 1);
      if (fairnessScore > bestScore) {
        bestScore = fairnessScore;
        pick = band;
      }
    }
    if (pick === -1) break;
    cycle.push(pick as 0 | 1 | 2);
    remaining[pick] -= 1;
    used[pick] += 1;
  }

  return cycle;
}

export function getCafeListRank(
  cafe: CafeForSort,
  promotions: PromotionForSort[],
  nowIso = new Date().toISOString()
): { band: 0 | 1 | 2; score: number; rotationScore: number } {
  const active = promotions.filter((p) => isActive(p, nowIso));
  const campaigns = active.filter((p) => !isOffer(p));
  const offers = active.filter((p) => isOffer(p));

  if (campaigns.length > 0) {
    const tier = normalizeTier(cafe.marketing_budget_tier);
    const tierMonthly = TIER_MONTHLY_PROXY[tier];
    const extraFees = campaigns.reduce((sum, p) => {
      const stored = Number(p.budget_total_jod);
      if (Number.isFinite(stored) && stored > 0) return sum + stored;
      let add = Number(p.setup_fee_monthly || 0);
      if (Number(p.performance_addon_enabled)) {
        add += Number(p.performance_fee_per_redeemed_order || 0) * 50;
      }
      if (String(p.campaign_placement || '').toLowerCase() === 'homepage') add += 40;
      return sum + add;
    }, 0);
    const score = tierMonthly * 1000 + campaigns.length * 100 + extraFees * 10 + TIER_MONTHLY_PROXY[tier];
    return { band: 0, score, rotationScore: dailyRotationScore(cafe.id, nowIso) };
  }

  if (offers.length > 0) {
    const score = offers.reduce((sum, p) => {
      const stored = Number(p.budget_total_jod);
      if (Number.isFinite(stored) && stored > 0) return sum + stored;
      const highlighted = String(p.offer_tier || '').toLowerCase() === 'highlighted';
      return sum + Number(p.setup_fee_monthly || 0) + (highlighted ? 30 : 0);
    }, 0);
    return { band: 1, score, rotationScore: dailyRotationScore(cafe.id, nowIso) };
  }

  return { band: 2, score: 0, rotationScore: dailyRotationScore(cafe.id, nowIso) };
}

function compareWithinBand<T extends CafeForSort>(
  a: { cafe: T; rank: { score: number; rotationScore: number } },
  b: { cafe: T; rank: { score: number; rotationScore: number } }
): number {
  if (Math.abs(b.rank.score - a.rank.score) > ROTATION_SCORE_TOLERANCE) {
    return b.rank.score - a.rank.score;
  }
  if (b.rank.rotationScore !== a.rank.rotationScore) {
    return b.rank.rotationScore - a.rank.rotationScore;
  }
  if (b.rank.score !== a.rank.score) return b.rank.score - a.rank.score;
  return String(a.cafe.name).localeCompare(String(b.cafe.name));
}

function pullNextFromBand<T extends CafeForSort>(
  buckets: Record<0 | 1 | 2, Array<{ cafe: T; rank: { score: number; rotationScore: number } }>>,
  preferredBand: 0 | 1 | 2
): { cafe: T; rank: { score: number; rotationScore: number } } | undefined {
  if (buckets[preferredBand].length > 0) return buckets[preferredBand].shift();
  if (buckets[0].length > 0) return buckets[0].shift();
  if (buckets[1].length > 0) return buckets[1].shift();
  if (buckets[2].length > 0) return buckets[2].shift();
  return undefined;
}

export function sortCafesForCustomerList<T extends CafeForSort>(
  cafes: T[],
  promotions: PromotionForSort[],
  nowIso = new Date().toISOString()
): T[] {
  const byCafe: Record<string, PromotionForSort[]> = {};
  promotions.forEach((p) => {
    if (!byCafe[p.cafe_id]) byCafe[p.cafe_id] = [];
    byCafe[p.cafe_id].push(p);
  });

  const ranked = [...cafes].map((cafe) => ({
    cafe,
    rank: getCafeListRank(cafe, byCafe[cafe.id] || [], nowIso),
  }));

  const buckets: Record<0 | 1 | 2, typeof ranked> = { 0: [], 1: [], 2: [] };
  ranked.forEach((row) => buckets[row.rank.band].push(row));

  buckets[0].sort(compareWithinBand);
  buckets[1].sort(compareWithinBand);
  buckets[2].sort(compareWithinBand);

  const cycle = buildBandSlotCycle({
    campaign: buckets[0].length,
    offer: buckets[1].length,
    silent: buckets[2].length,
  });

  const ordered: T[] = [];
  while (ordered.length < ranked.length) {
    for (const preferredBand of cycle) {
      if (ordered.length >= ranked.length) break;
      const next = pullNextFromBand(buckets, preferredBand);
      if (!next) break;
      ordered.push(next.cafe);
    }
  }

  return ordered;
}

export function pickFeaturedPromotionTitle(
  promotions: PromotionForSort[],
  nowIso = new Date().toISOString()
): string | null {
  const active = promotions.filter((p) => isActive(p, nowIso));
  const campaigns = active.filter((p) => !isOffer(p));
  const offers = active.filter((p) => isOffer(p));
  const pool = campaigns.length > 0 ? campaigns : offers;
  if (pool.length === 0) return null;
  const sorted = [...pool].sort((a, b) => {
    if (isOffer(a) && isOffer(b)) {
      const ah = String(a.offer_tier || '').toLowerCase() === 'highlighted' ? 1 : 0;
      const bh = String(b.offer_tier || '').toLowerCase() === 'highlighted' ? 1 : 0;
      if (bh !== ah) return bh - ah;
      return Number(b.setup_fee_monthly || 0) - Number(a.setup_fee_monthly || 0);
    }
    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });
  return sorted[0]?.title || null;
}

export function cafeHasActiveOffer(
  promotions: PromotionForSort[],
  nowIso = new Date().toISOString()
): boolean {
  return promotions.some((p) => isActive(p, nowIso) && isOffer(p));
}
